// util-btn
import { onDestroy, onMount } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";

const DURATION = 0.5;
const ARROW_DELAY = 0.06;
const EASE = "expo.out";
const PILL_SCALE = 0.96;

const NATIVE_INTERACTIVE = /^(a|button|input|select|textarea|summary|label)$/i;

export default function (element: HTMLElement) {
  const bg =
    element.querySelector<HTMLElement>('[util-btn="bg"]') ??
    element.querySelector<HTMLElement>(".about_util_btn_bg");
  const icons = Array.from(
    element.querySelectorAll<HTMLElement>('[util-btn="icon"]'),
  );
  if (!bg || icons.length < 2) return;

  const [iconA, iconB] = icons;

  icons.forEach((icon) => {
    icon.setAttribute("aria-hidden", "true");
    const svg = icon.querySelector("svg");
    if (svg) {
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
    }
  });

  const isNative = NATIVE_INTERACTIVE.test(element.tagName);
  const hadRole = element.hasAttribute("role");
  const hadTabindex = element.hasAttribute("tabindex");
  let onKeyDown: ((e: KeyboardEvent) => void) | null = null;

  if (!isNative) {
    if (!hadRole) element.setAttribute("role", "button");
    if (!hadTabindex) element.setAttribute("tabindex", "0");

    onKeyDown = (e: KeyboardEvent) => {
      if (e.key === " " || e.key === "Enter") {
        if (e.key === " ") e.preventDefault();
        element.click();
      }
    };
    element.addEventListener("keydown", onKeyDown);
  }

  onMount(() => {
    gsap.set(bg, { transformOrigin: "center center" });
    gsap.set(iconA, { xPercent: 0, yPercent: 0 });
    gsap.set(iconB, { xPercent: -100, yPercent: 100 });
  });

  const animate = (active: boolean) => {
    if (reduced) {
      gsap.set(bg, { scale: active ? PILL_SCALE : 1 });
      gsap.set(iconA, {
        xPercent: active ? 100 : 0,
        yPercent: active ? -100 : 0,
      });
      gsap.set(iconB, {
        xPercent: active ? 0 : -100,
        yPercent: active ? 0 : 100,
      });
      return;
    }
    gsap.to(bg, {
      scale: active ? PILL_SCALE : 1,
      duration: DURATION,
      ease: EASE,
      delay: active ? 0 : ARROW_DELAY,
      overwrite: "auto",
    });
    gsap.to(iconA, {
      xPercent: active ? 100 : 0,
      yPercent: active ? -100 : 0,
      duration: DURATION,
      ease: EASE,
      delay: active ? ARROW_DELAY : 0,
      overwrite: "auto",
    });
    gsap.to(iconB, {
      xPercent: active ? 0 : -100,
      yPercent: active ? 0 : 100,
      duration: DURATION,
      ease: EASE,
      delay: active ? ARROW_DELAY : 0,
      overwrite: "auto",
    });
  };

  const onEnter = () => animate(true);
  const onLeave = () => animate(false);

  element.addEventListener("mouseenter", onEnter);
  element.addEventListener("mouseleave", onLeave);
  element.addEventListener("focusin", onEnter);
  element.addEventListener("focusout", onLeave);

  onDestroy(() => {
    element.removeEventListener("mouseenter", onEnter);
    element.removeEventListener("mouseleave", onLeave);
    element.removeEventListener("focusin", onEnter);
    element.removeEventListener("focusout", onLeave);
    if (onKeyDown) element.removeEventListener("keydown", onKeyDown);

    if (!isNative) {
      if (!hadRole) element.removeAttribute("role");
      if (!hadTabindex) element.removeAttribute("tabindex");
    }

    gsap.killTweensOf([bg, iconA, iconB]);
    gsap.set([bg, iconA, iconB], { clearProps: "transform" });
  });
}
